import { api } from '@/lib/api';
import type { ApiReport, ApiAdminUser, BanUserDto, ChangeUserRoleDto } from '@/lib/types';

function normalizeList<T>(result: T[] | { items?: T[]; data?: T[] } | null | undefined): T[] {
  if (Array.isArray(result)) return result;
  if (result && 'items' in result && Array.isArray(result.items)) return result.items;
  if (result && 'data' in result && Array.isArray(result.data)) return result.data;
  return [];
}

// Si le backend est injoignable (réseau/CORS), on retombe sur des données de démo
// pour pouvoir prévisualiser l'interface d'administration sans serveur.
function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError || (err instanceof Error && /fetch|network/i.test(err.message));
}

// ---- Signalements ----

export async function getAdminReports(): Promise<ApiReport[]> {
  const result = await api<ApiReport[] | { items?: ApiReport[]; data?: ApiReport[] }>('/admin/reports');
  return normalizeList(result);
}

export async function getAdminReportById(id: string): Promise<ApiReport> {
  return api<ApiReport>(`/admin/reports/${id}`);
}

export async function resolveReport(id: string): Promise<void> {
  return api<void>(`/admin/reports/${id}/resolve`, { method: 'PATCH' });
}

export async function dismissReport(id: string): Promise<void> {
  return api<void>(`/admin/reports/${id}/dismiss`, { method: 'PATCH' });
}

export async function removeReportedContent(id: string): Promise<void> {
  return api<void>(`/admin/reports/${id}/remove-content`, { method: 'PATCH' });
}

// ---- Utilisateurs ----

export async function getAdminUsers(): Promise<ApiAdminUser[]> {
  const result = await api<ApiAdminUser[] | { items?: ApiAdminUser[]; data?: ApiAdminUser[] }>('/admin/users');
  return normalizeList(result);
}

export async function getAdminUserById(id: string): Promise<ApiAdminUser> {
  return api<ApiAdminUser>(`/admin/users/${id}`);
}

export async function banUser(id: string, dto: BanUserDto): Promise<void> {
  return api<void>(`/admin/users/${id}/ban`, {
    method: 'PATCH',
    body: JSON.stringify(dto),
  });
}

export async function unbanUser(id: string): Promise<void> {
  return api<void>(`/admin/users/${id}/unban`, { method: 'PATCH' });
}

export async function changeUserRole(id: string, dto: ChangeUserRoleDto): Promise<void> {
  return api<void>(`/admin/users/${id}/role`, {
    method: 'PATCH',
    body: JSON.stringify(dto),
  });
}


// Retire directement une activité en tant qu'Admin ou Moderator.
// Le backend vérifiera obligatoirement le rôle avant d'autoriser le retrait.
export async function removeActivityAsModerator(activityId: string): Promise<void> {
  return api<void>(`/admin/content/activities/${activityId}`, {
    method: 'DELETE',
  });
}


// Retire directement une publication Le Coin en tant qu'Admin ou Moderator.
// Le backend vérifie obligatoirement le rôle avant d'autoriser le retrait.
export async function removeCoinPostAsModerator(
  postId: string,
): Promise<void> {
  return api<void>(`/admin/content/coin-posts/${postId}`, {
    method: 'DELETE',
  });
}